
import { Cog8ToothIcon } from "@heroicons/vue/24/solid";
import { defineComponent } from "vue";
import { segmentAnalytics } from "@/assets/scripts";
import NavigationComponent from "@/components/layout/NavigationComponent.vue";
import PageHeader from "@/components/layout/PageHeader.vue";
import LoadingSection from "@/components/loading_state/LoadingSection.vue";
import { useCurrentProject } from "@/composables/project";
import DailyReportRepository from "@/repositories/DailyReportRepository";
import logger from "@/services/logger";
import { OverviewItem } from "@/types/DailyReport";
import BulkExport from "@/views/daily_report/components/BulkExport.vue";
import CalendarMonth from "@/views/daily_report/components/CalendarMonth.vue";

export default defineComponent({
  setup() {
    const project = useCurrentProject();
    return { project };
  },
  name: "DailyReportOverview",
  data() {
    return {
      days: [] as OverviewItem[],
      openExport: false,
      isLoading: false,
    };
  },
  mounted() {
    segmentAnalytics("daily_report_overview");
    this.getDailyReportOverview();
  },
  components: {
    PageHeader,
    NavigationComponent,
    CalendarMonth,
    BulkExport,
    LoadingSection,
    Cog8ToothIcon,
  },

  computed: {
    today() {
      return new Date();
    },
    date() {
      return String(this.today.getDate()).padStart(2, "0");
    },
    year() {
      return Number(this.$route.query.year as string) || null;
    },
    month() {
      return Number(this.$route.query.month as string) || null;
    },
  },
  watch: {
    year(newValue) {
      if (!newValue) {
        this.getDailyReportOverview();
      }
    },
  },
  methods: {
    getDailyReportOverview() {
      this.isLoading = true;

      return DailyReportRepository.getDailyReportOverview(
        this.currentCustomerName,
        this.currentSiteId,
        this.year,
        this.month,
      )
        .then((data) => {
          this.days = data.entries;
          if (!this.year && !this.month) {
            this.$router.replace({
              query: { ...this.$route.query, year: data.year, month: data.month },
            });
          }
        })
        .catch((error) => {
          logger.error(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    async nextMonth() {
      if (!this.year || !this.month) {
        return;
      }
      let year = this.year;
      let month = this.month;
      if (month === 12) {
        year = year + 1;
        month = 1;
      } else {
        month = month + 1;
      }
      await this.$router.replace({
        query: { ...this.$route.query, year: year, month: month },
      });
      this.getDailyReportOverview();
    },

    async prevMonth() {
      if (!this.year || !this.month) {
        return;
      }
      let year = this.year;
      let month = this.month;
      if (month === 1) {
        year = year - 1;
        month = 12;
      } else {
        month = month - 1;
      }
      await this.$router.replace({
        query: { ...this.$route.query, year: year, month: month },
      });
      this.getDailyReportOverview();
    },

    async resetPag() {
      await this.$router.replace({
        query: {},
      });
      this.getDailyReportOverview();
    },

    initializeReport(date: string) {
      const { customer_name, site_id } = this.$route.params;

      return DailyReportRepository.initializeDailyReport(
        customer_name as string,
        site_id as string,
        date,
      )
        .then((data) => {
          const item = this.days.find((item) => item.date === date);
          if (item) {
            item.data = data;
          }
        })
        .catch((error) => {
          logger.error(error);
        });
    },
    deleteReport(date: string) {
      const { customer_name, site_id } = this.$route.params;

      return DailyReportRepository.deleteDailyReport(
        customer_name as string,
        site_id as string,
        date,
      )
        .then(() => {
          const item = this.days.find((item) => item.date === date);
          if (item) {
            item.data.report.status = "NOT_AVAILABLE";
          }
        })
        .catch((error) => {
          logger.error(error);
        });
    },
  },
});
